/**
 * Hero Section Component (Material UI)
 * Main landing page hero with value proposition
 */
import { Link } from 'react-router-dom';
import { Box, Container, Typography, Button, Stack } from '@mui/material';
import { useCustomTranslation } from '../../hooks';
import { Calculate as CalculateIcon, TrendingUp as TrendingIcon } from '@mui/icons-material';

export default function HeroMUI() {
  const { t, i18n } = useCustomTranslation();
  const isEnglish = i18n.language === 'en';

  return (
    <Box
      sx={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        py: { xs: 10, md: 14 },
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
          {/* Main Heading */}
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2.25rem', sm: '2.75rem', md: '3.5rem' },
              fontWeight: 800,
              mb: 3,
              letterSpacing: '-0.02em',
              lineHeight: 1.1,
            }}
          >
            {t('home.hero.title')}
          </Typography>

          {/* Subtitle */}
          <Typography
            variant="h5"
            sx={{
              fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.375rem' },
              mb: 5,
              opacity: 0.95,
              fontWeight: 400,
              lineHeight: 1.6,
              maxWidth: 700,
              mx: 'auto',
            }}
          >
            {t('home.hero.subtitle')}
          </Typography>

          {/* Call to Action Buttons */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="center"
            sx={{ mb: 8 }}
          >
            <Button
              component={Link}
              to="/blog"
              variant="contained"
              size="large"
              startIcon={<TrendingIcon />}
              sx={{
                bgcolor: 'white',
                color: '#fff',
                px: 4,
                py: 1.75,
                fontSize: '1.125rem',
                fontWeight: 600,
                boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.95)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                },
              }}
            >
              {isEnglish ? 'Read Latest Insights' : '閱讀最新資訊'}
            </Button>
            <Button
              component={Link}
              to="#calculator-suite"
              variant="outlined"
              size="large"
              startIcon={<CalculateIcon />}
              onClick={(e) => {
                e.preventDefault();
                const element = document.getElementById('calculator-suite');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
              sx={{
                borderColor: 'white',
                borderWidth: 2,
                color: 'white',
                px: 4,
                py: 1.75,
                fontSize: '1.125rem',
                fontWeight: 600,
                '&:hover': {
                  borderColor: 'white',
                  borderWidth: 2,
                  bgcolor: 'rgba(255,255,255,0.15)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              {isEnglish ? 'Try Calculators' : '試用計算機'}
            </Button>
          </Stack>

          {/* Stats */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
              gap: 4,
              pt: 2,
            }}
          >
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, fontSize: '2.5rem' }}>
                17
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  opacity: 0.9,
                  textTransform: 'uppercase',
                  letterSpacing: 1.5,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                }}
              >
                {isEnglish ? 'Blog Articles' : '博客文章'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, fontSize: '2.5rem' }}>
                12
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  opacity: 0.9,
                  textTransform: 'uppercase',
                  letterSpacing: 1.5,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                }}
              >
                {t('home.stats.calculators')}
              </Typography>
            </Box>
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, fontSize: '2.5rem' }}>
                100%
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  opacity: 0.9,
                  textTransform: 'uppercase',
                  letterSpacing: 1.5,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                }}
              >
                {t('home.stats.free')}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
